import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Radio,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  ensurePortalEnvelope,
  serializePortalDraft,
  tryParsePortalDraft,
  type PortalArticlePayload,
  type PortalContentBlock,
} from '../../portal/portalDraftEnvelope';
import type { DraftNotebookKind } from '../../utils/draftNotebookKind';
import { DraftSideInsights, draftBodyForInsight } from './DraftSideInsights';
import {
  DEFAULT_ARTICLE_CATEGORIES,
  DEFAULT_ARTICLE_TAG_POOL,
  normalizeArticleCategory,
  sanitizeArticleTagsAgainstPool,
} from '../../constants/articleEditorConstants';
import { fetchCompanyProfile } from '../../api/companyProfile';
import { articlePayloadPlainText, plainTextStats } from '../../utils/draftTextStats';
import { ArticleBodyEditorJs } from './ArticleBodyEditorJs';
import { LibraryLinkedContentRail } from './LibraryLinkedContentRail';
import type { FrontendNotebook } from '../../contexts/NotebooksContext';
import { analyzeDraftSentiment, type DraftSentiment } from '../../utils/draftSentiment';
import type { ContrastingView, SocialMediaPost } from '../../contexts/NotebooksContext';
import type { NewsCluster, WikiConcept } from '../../types/types';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
};

/** Match library drawer analytic-profile control height (~36px). */
const METADATA_CONTROL_HEIGHT_PX = 36;
const metadataCompactControlSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fafafa',
    minHeight: METADATA_CONTROL_HEIGHT_PX,
    maxHeight: METADATA_CONTROL_HEIGHT_PX,
    fontSize: '0.8125rem',
    py: 0,
    '& fieldset': { borderColor: '#3f3f46' },
    '&:hover fieldset': { borderColor: '#a1a1aa' },
    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
  },
  '& .MuiOutlinedInput-input': {
    py: '6px',
    boxSizing: 'border-box',
  },
  '& .MuiInputLabel-root': { color: '#a1a1aa', fontSize: '0.8rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
    right: 6,
  },
};

function contrastingViewIsExpressed(v: ContrastingView) {
  return String(v.point_of_view_type || '').toLowerCase() === 'expressed';
}

/** Ignore read_time/updated noise when the library echoes a save back into `draftContent`. */
function articlePortalRemoteSyncEqual(
  prev: PortalArticlePayload,
  incoming: PortalArticlePayload,
  articleCategories: string[]
): boolean {
  const pc = normalizeArticleCategory(prev.category, articleCategories);
  const ic = normalizeArticleCategory(incoming.category, articleCategories);
  return (
    prev.title === incoming.title &&
    prev.excerpt === incoming.excerpt &&
    prev.content === incoming.content &&
    JSON.stringify(prev.content_blocks) === JSON.stringify(incoming.content_blocks) &&
    pc === ic &&
    JSON.stringify(prev.tags) === JSON.stringify(incoming.tags) &&
    (prev.author || '') === (incoming.author || '') &&
    (prev.og_image || '') === (incoming.og_image || '') &&
    (prev.id || '') === (incoming.id || '')
  );
}

function ContrastingViewRow({ view }: { view: ContrastingView }) {
  const expressed = contrastingViewIsExpressed(view);
  return (
    <Card sx={{ bgcolor: '#0a0a0a', border: '1px solid #27272a', mb: 1 }}>
      <CardContent
        sx={{
          py: 1,
          px: 1.25,
          '&:last-child': { pb: 1 },
          display: 'flex',
          gap: 1.25,
          alignItems: 'flex-start',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 12,
            height: 12,
            mt: 0.35,
            borderRadius: '50%',
            flexShrink: 0,
            border: '1.5px solid #71717a',
            bgcolor: expressed ? '#bbf7d0' : 'transparent',
            boxSizing: 'border-box',
          }}
        />
        <Typography variant="body2" sx={{ color: '#d4d4d8', fontSize: '0.8rem' }}>
          {view.point_of_view_description}
        </Typography>
      </CardContent>
    </Card>
  );
}

export type ArticlePortalDraftPanelProps = {
  notebookId: string;
  draftTitle: string;
  draftContent: string;
  onTitleChange: (title: string) => void;
  onSaveSerialized: (title: string, serialized: string) => void;
  readOnly?: boolean;
  clusters: NewsCluster[];
  clustersLoading: boolean;
  clusterIds?: string[];
  onOpenClusterDetails: (event: React.MouseEvent, cluster: NewsCluster) => void;
  isClusterContentGenerating: (clusterTitle: string, notebookId?: string) => boolean;
  selectedClusterTitleForDraft: string | null;
  onSelectClusterTitleForDraft: (title: string | null) => void;
  onGenerateArticleDraftFromCluster: () => void | Promise<void>;
  isGeneratingArticleDraftFromCluster: boolean;
  socialMediaPosts?: SocialMediaPost[] | null;
  contrastingViews?: ContrastingView[] | null;
  linkedNotebookIds: string[];
  allNotebooks: FrontendNotebook[];
  activeNotebooks: FrontendNotebook[];
  onLinkedNotebookIdsChange: (ids: string[]) => Promise<void>;
  onAddClustersToDraft: (clusters: NewsCluster[]) => Promise<boolean>;
  onRemoveClusterFromDraft: (clusterTitle: string) => Promise<boolean>;
  /** Wiki entities manually linked to this article (persisted on draft). */
  draftLinkedWikiEntities?: WikiConcept[];
  onDraftLinkedWikiEntitiesChange?: (next: WikiConcept[]) => Promise<void>;
};

export function ArticlePortalDraftPanel({
  notebookId,
  draftTitle,
  draftContent,
  onTitleChange,
  onSaveSerialized,
  readOnly = false,
  clusters,
  clustersLoading,
  clusterIds,
  onOpenClusterDetails,
  isClusterContentGenerating,
  selectedClusterTitleForDraft,
  onSelectClusterTitleForDraft,
  onGenerateArticleDraftFromCluster,
  isGeneratingArticleDraftFromCluster,
  socialMediaPosts: _socialMediaPosts,
  contrastingViews,
  linkedNotebookIds,
  allNotebooks,
  activeNotebooks,
  onLinkedNotebookIdsChange,
  onAddClustersToDraft,
  onRemoveClusterFromDraft,
  draftLinkedWikiEntities = [],
  onDraftLinkedWikiEntitiesChange,
}: ArticlePortalDraftPanelProps) {
  const [articleCategories, setArticleCategories] = useState<string[]>(() => [...DEFAULT_ARTICLE_CATEGORIES]);
  const [articleTagPool, setArticleTagPool] = useState<string[]>(() => [...DEFAULT_ARTICLE_TAG_POOL]);

  const [p, setP] = useState<PortalArticlePayload>(() => {
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, 'article' as DraftNotebookKind);
    if (e.kind === 'article') {
      const pl = e.payload;
      return {
        ...pl,
        category: normalizeArticleCategory(pl.category, [...DEFAULT_ARTICLE_CATEGORIES]),
        tags: sanitizeArticleTagsAgainstPool(pl.tags, DEFAULT_ARTICLE_TAG_POOL),
      };
    }
    throw new Error('expected article envelope');
  });

  const [metadataOpen, setMetadataOpen] = useState(false);
  const [railTab, setRailTab] = useState(0);
  const [insightSubTab, setInsightSubTab] = useState(0);
  const [contrastingPovTab, setContrastingPovTab] = useState(0);

  useEffect(() => {
    setRailTab((t) => (t > 2 ? 0 : t));
  }, [notebookId]);

  useEffect(() => {
    if (insightSubTab !== 1) setContrastingPovTab(0);
  }, [insightSubTab]);
  const [tagInput, setTagInput] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const payloadRef = useRef(p);
  payloadRef.current = p;

  const draftTitleRef = useRef(draftTitle);
  draftTitleRef.current = draftTitle;

  const normalizePayloadForSave = useCallback(
    (next: PortalArticlePayload): PortalArticlePayload => {
      const measure = articlePayloadPlainText(next);
      const stats = plainTextStats(measure);
      const { url: _drop, ...rest } = next;
      return {
        ...rest,
        category: normalizeArticleCategory(next.category, articleCategories),
        tags: sanitizeArticleTagsAgainstPool(next.tags, articleTagPool),
        read_time: `${stats.readingTimeMinutes} min`,
        updated: new Date().toISOString(),
      };
    },
    [articleCategories, articleTagPool]
  );

  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = useCallback(
    (next: PortalArticlePayload) => {
      if (readOnly) return;
      const normalized = normalizePayloadForSave(next);
      payloadRef.current = normalized;
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => {
        const cur = payloadRef.current;
        const title =
          cur.title?.trim() || draftTitleRef.current?.trim() || 'Untitled article';
        const withTitle = normalizePayloadForSave({ ...cur, title });
        payloadRef.current = withTitle;
        const env = { schemaVersion: 1 as const, kind: 'article' as const, payload: withTitle };
        onTitleChange(title);
        onSaveSerialized(title, serializePortalDraft(env));
      }, 850);
    },
    [onTitleChange, onSaveSerialized, readOnly, normalizePayloadForSave]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cp = await fetchCompanyProfile();
      if (cancelled || !cp) return;
      const cats = (cp.article_categories ?? [])
        .map((s) => String(s).trim())
        .filter((s) => Boolean(s));
      const tags = (cp.article_tags ?? [])
        .map((s) => String(s).trim())
        .filter((s) => Boolean(s));
      if (cats.length) setArticleCategories(cats);
      if (tags.length) setArticleTagPool(tags);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () => () => {
      if (t.current) clearTimeout(t.current);
    },
    []
  );

  useEffect(() => {
    const parsed = tryParsePortalDraft(draftContent);
    if (parsed?.kind === 'article') {
      const incoming: PortalArticlePayload = {
        ...parsed.payload,
        category: normalizeArticleCategory(parsed.payload.category, articleCategories),
        tags: sanitizeArticleTagsAgainstPool(parsed.payload.tags, articleTagPool),
      };
      setP((prev) => (articlePortalRemoteSyncEqual(prev, incoming, articleCategories) ? prev : incoming));
      return;
    }
    const e = ensurePortalEnvelope(draftContent, draftTitle, notebookId, 'article');
    if (e.kind === 'article') {
      const incoming: PortalArticlePayload = {
        ...e.payload,
        category: normalizeArticleCategory(e.payload.category, articleCategories),
        tags: sanitizeArticleTagsAgainstPool(e.payload.tags, articleTagPool),
      };
      setP((prev) => (articlePortalRemoteSyncEqual(prev, incoming, articleCategories) ? prev : incoming));
    }
  }, [notebookId, draftContent, draftTitle, articleCategories, articleTagPool]);

  const push = (next: PortalArticlePayload) => {
    setP(next);
    if (!readOnly) scheduleSave(next);
  };

  const pushRef = useRef(push);
  pushRef.current = push;

  const handleArticleBodyChange = useCallback((content: string, content_blocks: PortalContentBlock[]) => {
    const cur = payloadRef.current;
    pushRef.current({ ...cur, content, content_blocks });
  }, []);

  const handleArticleBodyBlurValidation = useCallback(() => {
    setShowErrors(true);
  }, []);

  const measureText = useMemo(() => articlePayloadPlainText(p), [p]);
  const stats = useMemo(() => plainTextStats(measureText), [measureText]);

  const [sentiment, setSentiment] = useState<DraftSentiment>(() => ({
    label: 'Neutral / mixed',
    summary: 'Analyzing draft…',
    positiveHits: 0,
    negativeHits: 0,
  }));

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      void analyzeDraftSentiment(measureText).then((next) => {
        if (!cancelled) setSentiment(next);
      });
    }, 160);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [measureText]);

  const titleOk = Boolean(p.title?.trim());
  const excerptOk = Boolean(p.excerpt?.trim());
  const bodyOk = Boolean(
    (p.content || '').trim() ||
      (p.content_blocks || []).some(
        (b) => (b.text || '').trim() || (b.html || '').trim() || (b.src || '').trim()
      )
  );

  const titleFieldErr = showErrors && !titleOk;
  const excerptFieldErr = showErrors && !excerptOk;
  const bodyFieldErr = showErrors && !bodyOk;

  const tagOptions = useMemo(
    () => articleTagPool.filter((opt) => !p.tags.includes(opt)),
    [p.tags, articleTagPool]
  );

  const resolvedCategory = useMemo(
    () => normalizeArticleCategory(p.category, articleCategories),
    [p.category, articleCategories]
  );
  const categoryOnCuratedList = useMemo(
    () => articleCategories.some((c) => c.toLowerCase() === resolvedCategory.toLowerCase()),
    [articleCategories, resolvedCategory]
  );
  /** Category is always normalized to the list; tags are the usual gap for new drafts. */
  const metadataComplete = categoryOnCuratedList && p.tags.length >= 1;

  const hasContrasting = Boolean(contrastingViews?.length);

  const contrastingExpressed = useMemo(
    () => (contrastingViews || []).filter((v) => contrastingViewIsExpressed(v)),
    [contrastingViews]
  );
  const contrastingSuggested = useMemo(
    () =>
      (contrastingViews || []).filter((v) => String(v.point_of_view_type || '').toLowerCase() === 'suggested'),
    [contrastingViews]
  );

  const sentimentFaceRows = useMemo(
    () => [
      { emoji: '😊', active: sentiment.label.startsWith('Leans positive') },
      { emoji: '😐', active: sentiment.label.includes('Neutral') },
      { emoji: '😟', active: sentiment.label.startsWith('Leans negative') },
    ],
    [sentiment.label]
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box
        component="div"
        sx={{
          border: 'none',
          m: 0,
          p: 0,
          minWidth: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {titleFieldErr ? (
          <Typography variant="caption" sx={{ color: '#f87171', display: 'block', mb: 1 }}>
            Title is required — edit it in the drawer header.
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: 'stretch',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: { xs: 1, md: '0 0 60%' },
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
              mb: 0.5,
              flexWrap: 'wrap',
              rowGap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                flex: '1 1 auto',
              }}
            >
              {metadataComplete ? (
                <CheckCircleIcon
                  sx={{ color: '#22c55e', fontSize: 22, flexShrink: 0 }}
                  titleAccess="Category and tags are set — no further edits required here."
                />
              ) : (
                <WarningAmberIcon
                  sx={{ color: '#fbbf24', fontSize: 22, flexShrink: 0 }}
                  titleAccess="Select a category and at least one tag from the curated list."
                />
              )}
              {!metadataOpen ? (
                <>
                  <Chip
                    size="small"
                    label={resolvedCategory}
                    sx={{ bgcolor: '#27272a', color: '#e4e4e7', fontWeight: 600, flexShrink: 0 }}
                  />
                  {p.tags.length > 0 ? (
                    <>
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{ borderColor: '#3f3f46', alignSelf: 'stretch', my: 0.25 }}
                      />
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        {p.tags.map((tag) => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            sx={{ bgcolor: '#27272a', color: '#60a5fa', flexShrink: 0 }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : null}
                </>
              ) : null}
            </Box>
            <Button
              size="small"
              disabled={readOnly}
              endIcon={
                <ExpandMoreIcon
                  sx={{ transition: '0.2s', transform: metadataOpen ? 'rotate(180deg)' : 'none', color: '#a1a1aa' }}
                />
              }
              onClick={() => setMetadataOpen((v) => !v)}
              sx={{ color: '#60a5fa', textTransform: 'none', py: 0, minWidth: 'auto', ml: { xs: 0, sm: 'auto' }, flexShrink: 0 }}
            >
              {metadataOpen ? 'Hide' : 'Show'} metadata
            </Button>
          </Box>
          <Collapse in={metadataOpen}>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                border: '1px solid #27272a',
                borderRadius: 1,
                bgcolor: '#171717',
              }}
            >
              <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block', fontSize: '0.7rem', mb: 1.5 }}>
                Select a category and up to five tags from the curated list.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.25 }} required>
                <InputLabel id="article-cat-label" sx={{ color: '#a1a1aa', fontSize: '0.8rem' }}>
                  Category
                </InputLabel>
                <Select
                  labelId="article-cat-label"
                  label="Category"
                  size="small"
                  disabled={readOnly}
                  value={normalizeArticleCategory(p.category, articleCategories)}
                  onChange={(e: SelectChangeEvent) => push({ ...p, category: e.target.value })}
                  sx={{
                    color: '#fafafa',
                    minHeight: METADATA_CONTROL_HEIGHT_PX,
                    maxHeight: METADATA_CONTROL_HEIGHT_PX,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#3f3f46' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a1a1aa' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#60a5fa' },
                    '& .MuiSelect-select': {
                      py: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: METADATA_CONTROL_HEIGHT_PX - 2,
                      boxSizing: 'border-box',
                    },
                  }}
                >
                  {articleCategories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Autocomplete
                freeSolo={false}
                size="small"
                options={tagOptions}
                inputValue={tagInput}
                onInputChange={(_, v) => setTagInput(v)}
                value={null}
                onChange={(_, v) => {
                  if (typeof v === 'string' && v && p.tags.length < 5) {
                    const canon = articleTagPool.find((x) => x.toLowerCase() === v.toLowerCase());
                    if (canon && !p.tags.includes(canon)) {
                      push({ ...p, tags: [...p.tags, canon] });
                    }
                  }
                  setTagInput('');
                }}
                disabled={readOnly || p.tags.length >= 5}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Tags"
                    placeholder={p.tags.length >= 5 ? 'Maximum five tags' : 'Type to search tags…'}
                    helperText={`${p.tags.length}/5 selected — only listed tags are allowed.`}
                    FormHelperTextProps={{ sx: { mt: 0.5, mb: 0, fontSize: '0.7rem' } }}
                    sx={{ ...fieldSx, ...metadataCompactControlSx }}
                  />
                )}
              />
              {p.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                  {p.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={
                        readOnly
                          ? undefined
                          : () => push({ ...p, tags: p.tags.filter((t) => t !== tag) })
                      }
                      sx={{ bgcolor: '#27272a', color: '#60a5fa' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label="Excerpt"
          value={p.excerpt}
          error={excerptFieldErr}
          helperText={excerptFieldErr ? 'Excerpt is required' : ' '}
          onChange={(e) => push({ ...p, excerpt: e.target.value })}
          onBlur={() => setShowErrors(true)}
          disabled={readOnly}
          sx={{ ...fieldSx, mb: 1, flexShrink: 0 }}
        />

            <Typography
              component="label"
              variant="body2"
              sx={{
                color: bodyFieldErr ? '#f87171' : '#a1a1aa',
                mb: 0.75,
                fontSize: '0.75rem',
                display: 'block',
              }}
            >
              Body&nbsp;*
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 220,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                isolation: 'isolate',
                border: `1px solid ${bodyFieldErr ? '#f87171' : '#3f3f46'}`,
                borderRadius: 1,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: bodyFieldErr ? '#f87171' : '#a1a1aa' },
                '&:focus-within': { borderColor: bodyFieldErr ? '#f87171' : '#60a5fa' },
              }}
            >
              <ArticleBodyEditorJs
                key={notebookId}
                notebookId={notebookId}
                readOnly={readOnly}
                seed={{ content: p.content, content_blocks: p.content_blocks }}
                onBlurValidation={handleArticleBodyBlurValidation}
                onBodyChange={handleArticleBodyChange}
              />
            </Box>
            {bodyFieldErr ? (
              <Typography variant="caption" sx={{ color: '#f87171', mt: 0.5, display: 'block' }}>
                Body is required
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ visibility: 'hidden', mt: 0.5, display: 'block' }}>
                &nbsp;
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: { xs: 1, md: '0 0 40%' },
              minWidth: { xs: '100%', md: 200 },
              minHeight: 0,
              overflow: 'auto',
              borderLeft: { md: '1px solid #27272a' },
              pl: { md: 2 },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DraftSideInsights title={p.title || draftTitle} excerpt={p.excerpt || ''} body={draftBodyForInsight(p)} />
            <Tabs
              value={railTab}
              onChange={(_, v) => setRailTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                alignSelf: 'stretch',
                width: '100%',
                minHeight: 40,
                '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
                '& .MuiTab-root': { color: '#a1a1aa', minHeight: 40, py: 0.5, fontSize: '0.75rem' },
                '& .Mui-selected': { color: '#fafafa' },
                '& .MuiTabs-indicator': { backgroundColor: '#60a5fa' },
              }}
            >
              <Tab label="Feedback" />
              <Tab label="Insights" />
              <Tab label="Linked content" />
            </Tabs>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                pt: 1.5,
                pr: 0.5,
                ...(railTab === 2
                  ? { overflow: 'hidden', display: 'flex', flexDirection: 'column' }
                  : { overflow: 'auto' }),
              }}
            >
              {railTab === 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 0.5 }}>
                    Words: <strong>{stats.words}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 0.5 }}>
                    Characters: <strong>{stats.characters}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d4d4d8', mb: 1.5 }}>
                    Est. reading time: <strong>{stats.readingTimeMinutes} min</strong> (230 wpm)
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      rowGap: 0.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#fafafa' }}>
                      Sentiment
                    </Typography>
                    <Chip label={sentiment.label} size="small" sx={{ bgcolor: '#27272a', color: '#e4e4e7' }} />
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 0.5 } }}>
                      {sentimentFaceRows.map((row) => (
                        <Box
                          key={row.emoji}
                          component="span"
                          sx={{
                            fontSize: '1.2rem',
                            lineHeight: 1,
                            cursor: 'default',
                            opacity: row.active ? 1 : 0.38,
                            filter: row.active ? 'none' : 'grayscale(35%)',
                          }}
                        >
                          {row.emoji}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
              {railTab === 1 && (
                <Box>
                  <Tabs
                    value={insightSubTab}
                    onChange={(_, v) => setInsightSubTab(v)}
                    sx={{
                      alignSelf: 'stretch',
                      width: '100%',
                      minHeight: 36,
                      mb: 1,
                      '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
                      '& .MuiTab-root': { color: '#a1a1aa', minHeight: 36, fontSize: '0.7rem' },
                      '& .Mui-selected': { color: '#fafafa' },
                      '& .MuiTabs-indicator': { backgroundColor: '#a78bfa' },
                    }}
                  >
                    <Tab label="Related clusters" />
                    <Tab label="Contrasting views" />
                  </Tabs>
                  {insightSubTab === 0 && (
                    <Box>
                      {clustersLoading && (
                        <Box display="flex" alignItems="center" gap={1} py={1}>
                          <CircularProgress size={22} sx={{ color: '#60a5fa' }} />
                          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                            Loading…
                          </Typography>
                        </Box>
                      )}
                      {!clustersLoading && clusters.length > 0 && (
                        <>
                          <List dense disablePadding sx={{ mb: 1.5 }}>
                            {clusters.map((cluster, index) => (
                              <ListItem
                                key={cluster.title}
                                button
                                onClick={(e) => onOpenClusterDetails(e, cluster)}
                                divider={index < clusters.length - 1}
                                sx={{
                                  bgcolor: '#0a0a0a',
                                  mb: 1,
                                  borderRadius: 1,
                                  border: '1px solid #27272a',
                                  '&:hover': { bgcolor: '#1a1a1a' },
                                  /* ListItemSecondaryAction is absolutely positioned; reserve space so text never runs under icons */
                                  pr: isClusterContentGenerating(cluster.title, notebookId) ? '132px' : '108px',
                                  boxSizing: 'border-box',
                                }}
                              >
                                <ListItemText
                                  primary={cluster.title}
                                  secondary={`${(cluster.general_overview || '').substring(0, 80)}…`}
                                  primaryTypographyProps={{
                                    sx: {
                                      color: '#fafafa',
                                      fontSize: '0.85rem',
                                      pr: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    },
                                  }}
                                  secondaryTypographyProps={{
                                    sx: {
                                      color: '#a1a1aa',
                                      fontSize: '0.7rem',
                                      pr: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                    },
                                  }}
                                />
                                <ListItemSecondaryAction>
                                  {isClusterContentGenerating(cluster.title, notebookId) && (
                                    <CircularProgress size={18} sx={{ color: '#60a5fa', mr: 0.5 }} />
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={(e) => onOpenClusterDetails(e, cluster)}
                                    sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <Radio
                                    size="small"
                                    checked={selectedClusterTitleForDraft === cluster.title}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() =>
                                      onSelectClusterTitleForDraft(
                                        selectedClusterTitleForDraft === cluster.title ? null : cluster.title
                                      )
                                    }
                                    value={cluster.title}
                                    sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#60a5fa' } }}
                                  />
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => void onGenerateArticleDraftFromCluster()}
                            disabled={!selectedClusterTitleForDraft || isGeneratingArticleDraftFromCluster || readOnly}
                            sx={{
                              bgcolor: '#60a5fa',
                              color: '#0a0a0a',
                              '&:hover': { bgcolor: '#3b82f6' },
                              '&.Mui-disabled': { bgcolor: '#27272a', color: '#52525b' },
                            }}
                          >
                            {isGeneratingArticleDraftFromCluster ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                <CircularProgress size={16} sx={{ color: '#0a0a0a' }} />
                                Generating…
                              </Box>
                            ) : (
                              'Generate draft'
                            )}
                          </Button>
                        </>
                      )}
                      {!clustersLoading && clusters.length === 0 && (
                        <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                          {clusterIds && clusterIds.length > 0
                            ? 'No cluster data loaded.'
                            : 'No associated clusters to this piece of content.'}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {insightSubTab === 1 && (
                    <Box>
                      {!hasContrasting ? (
                        <Typography variant="body2" sx={{ color: '#71717a' }}>
                          No contrasting views on this draft yet.
                        </Typography>
                      ) : (
                        <>
                          <Tabs
                            value={contrastingPovTab}
                            onChange={(_, v) => setContrastingPovTab(v)}
                            sx={{
                              alignSelf: 'stretch',
                              width: '100%',
                              minHeight: 36,
                              mb: 1,
                              '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
                              '& .MuiTab-root': { color: '#a1a1aa', minHeight: 36, fontSize: '0.75rem' },
                              '& .Mui-selected': { color: '#fafafa' },
                              '& .MuiTabs-indicator': { backgroundColor: '#34d399' },
                            }}
                          >
                            <Tab label="Expressed" />
                            <Tab label="Suggested" />
                          </Tabs>
                          {contrastingPovTab === 0 &&
                            (contrastingExpressed.length === 0 ? (
                              <Typography variant="body2" sx={{ color: '#71717a' }}>
                                No perspectives marked as expressed in the article.
                              </Typography>
                            ) : (
                              contrastingExpressed.map((view, index) => (
                                <ContrastingViewRow key={`e-${index}`} view={view} />
                              ))
                            ))}
                          {contrastingPovTab === 1 &&
                            (contrastingSuggested.length === 0 ? (
                              <Typography variant="body2" sx={{ color: '#71717a' }}>
                                No suggested perspectives yet.
                              </Typography>
                            ) : (
                              contrastingSuggested.map((view, index) => (
                                <ContrastingViewRow key={`s-${index}`} view={view} />
                              ))
                            ))}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {railTab === 2 && (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <LibraryLinkedContentRail
                    mode="notebook"
                    subjectId={notebookId}
                    readOnly={readOnly}
                    linkedNotebookIds={linkedNotebookIds}
                    onLinkedNotebookIdsChange={onLinkedNotebookIdsChange}
                    allNotebooks={allNotebooks}
                    activeNotebooks={activeNotebooks}
                    linkedClusters={clusters}
                    onOpenClusterDetails={onOpenClusterDetails}
                    onRemoveCluster={onRemoveClusterFromDraft}
                    draftLinkedWikiEntities={draftLinkedWikiEntities}
                    onDraftLinkedWikiEntitiesChange={onDraftLinkedWikiEntitiesChange}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
